/*
 * ===============================================================================================================
 *                                Copyright 2023, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Button, TextField, List, ListItem, ListItemText, Autocomplete, Alert } from '@mui/material';
import { ListUser } from '@jda/lui-common-icon-library-mui5';
import FormDialog from '../../shared/Dialog/FormDialog';
import Loader from '../../shared/Loader';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { fetchRolesToAssign } from '../../store/user/users';
import { selectRolesToAssign } from '../../store/user/userSelector';
import { selectAppLocation } from '../../store/appLocation/appLocationSelector';
import { displaySnackbar, hideSnackbar } from '../../store/snackBar/snackBarActionCreator';
import { selectError } from '../../store/error/errorSelector';
import { assignRoleToSubject } from '../../services/roles';
import { ASSIGN_ROLE, ROLE, CANCEL_BTN_TEXT, ROLE_ASSIGNMENT_SUCCESS, ROLE_ASSIGNMENT_FAILURE } from './constants';
import { useStyles } from './styles';
import { IUser } from '../../models/Users/UserContracts';
import { IRole } from '../../models/Roles/RolesContracts';

interface RoleAssignmentDialogProps {
  selectedUsers: IUser[];
  onSuccess: () => void;
  onClose: () => void;
}

const RoleAssignmentDialog = ({ selectedUsers, onSuccess, onClose }: RoleAssignmentDialogProps) => {
  const rolesToAssign: IRole[] = useSelector(selectRolesToAssign);
  const appLocation = useSelector(selectAppLocation);
  const error = useSelector(selectError);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchRolesToAssign(dispatch, appLocation?.name);
  }, [dispatch, appLocation]);

  const [selectedRoleId, setSelectedRoleId] = useState<number>();
  const [isDialogLoaderShown, setIsDialogLoaderShown] = useState(false);
  const [usersWithErrors, setUsersWithErrors] = useState<IUser[]>();

  const { classes } = useStyles();

  const handleSubmit = async () => {
    setUsersWithErrors(undefined);
    setIsDialogLoaderShown(true);
    dispatch(hideSnackbar());

    const promiseResults = await Promise.all(
      selectedUsers.map(async user => {
        try {
          if (selectedRoleId) {
            await assignRoleToSubject({ roleId: selectedRoleId, subjectId: user.id });
          }
          return undefined;
        } catch (error: any) {
          // 409 is thrown when role is already assigned
          if (error.response?.status === 409) {
            return undefined;
          }

          return user;
        }
      })
    );

    const usersWithErrors = promiseResults.filter(
      (promiseResult): promiseResult is IUser => promiseResult !== undefined
    );

    if (usersWithErrors.length > 0) {
      setUsersWithErrors(usersWithErrors);
      setIsDialogLoaderShown(false);
      return;
    }

    onSuccess();
    onClose();
    dispatch(displaySnackbar(ROLE_ASSIGNMENT_SUCCESS, 'success'));
  };

  return (
    <FormDialog
      openModal={true}
      handleClose={onClose}
      maxWidth="xs"
      dialogTitle={
        <Box className={classes.title}>
          <ListUser /> {ASSIGN_ROLE}
        </Box>
      }
      dialogLoader={<Loader showLoader={isDialogLoaderShown} />}
      dialogAction1={
        <Button onClick={onClose} variant="outlined" color="primary">
          {CANCEL_BTN_TEXT}
        </Button>
      }
      dialogAction2={
        <Button
          onClick={handleSubmit}
          disabled={Boolean(!selectedRoleId || usersWithErrors)}
          variant="contained"
          color="primary"
        >
          {ASSIGN_ROLE}
        </Button>
      }
    >
      <Autocomplete
        size='small'
        options={rolesToAssign}
        onChange={(_event, option) => {
          setSelectedRoleId(option?.id);
        }}
        getOptionLabel={option => option.name}
        renderInput={params => <TextField {...params} label={ROLE} />}
      />
      {error.message && <ErrorMessage {...error} />}
      {usersWithErrors && (
        <Alert severity="error" className={classes.alert}>
          {ROLE_ASSIGNMENT_FAILURE}
          <List className={classes.userList}>
            {usersWithErrors.map(user => (
              <ListItem className={classes.userListItem} key={user.id}>
                <ListItemText>{user.userName}</ListItemText>
              </ListItem>
            ))}
          </List>
        </Alert>
      )}
    </FormDialog>
  );
};

export default RoleAssignmentDialog;
